'use client';

import React, { useEffect } from 'react';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useInViewportAnimation } from '@/hooks/useInViewportAnimation';
import HotProductSwiper from '@/app/(activity)/components/ActivityProductSwiper/HotProductSwiper';

interface HotProductSectionProps {
  hotProduct: {
    title: string;
    [key: string]: any;
  };
  hotProducts: any[];
  deviceType: EPlatform;
  isFromCache?: boolean;
  theme?: 'driving' | 'blueLight';
  onProductClick?: (product: any, index: number) => void;
  onProductExp?: (product: any, index: number) => void;
  className?: string;
  animationConfig?: {
    threshold?: number;
    triggerOnce?: boolean;
    enableFallback?: boolean;
    fallbackDelay?: number;
  };
}

export default function HotProductSection({
  hotProduct,
  hotProducts,
  deviceType,
  isFromCache = false,
  theme = 'blueLight',
  onProductClick,
  onProductExp,
  className = '',
  animationConfig = {
    threshold: 0.4,
    triggerOnce: true,
    enableFallback: true,
    fallbackDelay: 2000,
  },
}: HotProductSectionProps) {
  // 默认的产品点击处理
  const handleProductClick = (product: any, index: number) => {
    if (onProductClick) {
      onProductClick(product, index);
    }
  };

  // 默认的产品曝光处理
  const handleProductExp = (product: any, index: number) => {
    if (onProductExp) {
      onProductExp(product, index);
    }
    // TODO: 可以在这里添加通用的埋点逻辑
  };

  // 热卖商品动画触发器
  const isMobile = deviceType !== EPlatform.WEB;

  // 导航按钮立即显示逻辑（简化版）
  useEffect(() => {
    const timer = setTimeout(() => {
      // 直接触发导航按钮显示动画
      const navButtons = document.getElementsByClassName('hot-list-tab-div');

      Array.from(navButtons).forEach((element) => {
        const el = element as HTMLElement;

        // 如果是移动端或从缓存来的，直接显示
        if (isMobile || isFromCache) {
          el.style.opacity = '1';
          el.classList.remove('opacity0');
        } else {
          // PC端执行动画
          el.classList.add('blueTitleFadeInUpAnimate');
          setTimeout(() => {
            el.classList.remove('blueTitleFadeInUpAnimate');
            if (el.classList.contains('opacity0')) {
              el.classList.remove('opacity0');
            }
          }, 1000);
        }
      });

      // 同时处理标题
      const titles = document.getElementsByClassName('hot-list-title');
      Array.from(titles).forEach((element) => {
        const el = element as HTMLElement;
        if (isMobile || isFromCache) {
          el.style.opacity = '1';
          el.classList.remove('opacity0');
        } else {
          el.classList.add('blueTitleFadeInUpAnimate');
          setTimeout(() => {
            el.classList.remove('blueTitleFadeInUpAnimate');
            if (el.classList.contains('opacity0')) {
              el.classList.remove('opacity0');
            }
          }, 1000);
        }
      });

      // 确保轮播容器也显示
      const swiperContainer = document.getElementsByClassName(
        'blue-light-swiper-box',
      )?.[0] as HTMLElement;
      if (swiperContainer) {
        swiperContainer.style.opacity = '1';
      }
    }, 200); // 缩短到200ms

    return () => clearTimeout(timer);
  }, [isMobile, isFromCache]);

  const hotProductAnimationRef = useInViewportAnimation(
    () => {
      // 商品项的动画（保持原有逻辑）
      try {
        if (isFromCache || isMobile) {
          return;
        }

        // 商品项依次从下淡入 - 只对前5个商品添加动画（与Vue版本保持一致）
        setTimeout(() => {
          // 先显示轮播容器（与Vue版本逻辑一致）
          const swiperDom = document.getElementsByClassName('blue-light-swiper-box');

          // 设置所有轮播容器可见
          Array.from(swiperDom).forEach((container) => {
            const htmlEl = container as HTMLElement;
            htmlEl.style.opacity = '1';
            htmlEl.classList.remove('opacity0');
          });

          // 商品项动画
          const productItems = document.getElementsByClassName('hot-list-product-item');
          const firstFiveItems = Array.from(productItems).slice(0, 5);

          firstFiveItems.forEach((item, index) => {
            setTimeout(
              () => {
                item.classList.add('blueSwiperFadeInUpAnimate');
              },
              100 * (index + 1),
            );
          });

          // 性能检测和优化
          if ('requestIdleCallback' in window) {
            // 高性能设备：逐个动画
            firstFiveItems.forEach((item, index) => {
              setTimeout(
                () => {
                  item.classList.remove('hasAnimate');
                },
                100 * (index + 1) + 500,
              );
            });
          } else {
            // 低性能设备：直接移除hasAnimate类
            firstFiveItems.forEach((el) => {
              el.classList.remove('hasAnimate');
            });
          }
        }, 100);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('HotProductSection animation error:', error);
        // 出错时确保内容可见
        const elements = document.querySelectorAll('.opacity0');
        elements.forEach((element) => {
          const el = element as HTMLElement;
          el.style.opacity = '1';
        });
      }
    },
    {
      threshold: animationConfig.threshold,
      once: animationConfig.triggerOnce,
    },
  );

  // 处理从缓存返回时的元素状态重置
  useEffect(() => {
    if (isFromCache || isMobile) {
      // 延迟一点确保DOM已渲染
      setTimeout(() => {
        const swiperContainer = document.getElementsByClassName(
          'blue-light-swiper-box',
        )?.[0] as HTMLElement;
        if (swiperContainer) {
          swiperContainer.style.opacity = '1';
        }

        // 移除所有可能的opacity0类和重置透明度
        const elementsWithOpacity = document.querySelectorAll('.opacity0');
        elementsWithOpacity.forEach((element) => {
          const el = element as HTMLElement;
          el.classList.remove('opacity0');
          el.style.opacity = '1';
        });

        // 确保其他需要显示的元素也是可见的
        const animationElements = ['hot-list-title', 'hot-list-tab-div', 'hot-list-product-item'];

        // 特别处理前5个商品项（与动画逻辑保持一致）
        const productItems = document.getElementsByClassName('hot-list-product-item');
        const firstFiveItems = Array.from(productItems).slice(0, 5);
        firstFiveItems.forEach((element) => {
          const htmlEl = element as HTMLElement;
          htmlEl.style.opacity = '1';
          htmlEl.style.transform = 'translateY(0)';
          htmlEl.classList.remove('blueSwiperFadeInUpAnimate', 'opacity0', 'hasAnimate');
        });

        animationElements.forEach((classNameItem) => {
          const elements = document.getElementsByClassName(classNameItem);
          Array.from(elements).forEach((element) => {
            const htmlEl = element as HTMLElement;
            htmlEl.style.opacity = '1';
            htmlEl.style.transform = 'translateY(0)';
            htmlEl.classList.remove('fadeInDownAnimate', 'fadeInUpAnimate', 'opacity0');
          });
        });
      }, 50);
    }
  }, [isFromCache, isMobile]);

  // 备用显示机制 - 如果动画没有触发，强制显示商品
  useEffect(() => {
    if (!animationConfig.enableFallback) return;

    const fallbackTimer = setTimeout(() => {
      const swiperDom = document.getElementsByClassName('blue-light-swiper-box');

      if (swiperDom[0]) {
        const swiperEl = swiperDom[0] as HTMLElement;
        swiperEl.style.opacity = '1';
        swiperEl.classList.remove('opacity0');
      }

      // 也确保商品项可见
      const productItems = document.getElementsByClassName('hot-list-product-item');
      Array.from(productItems).forEach((el) => {
        const htmlEl = el as HTMLElement;
        htmlEl.classList.remove('hasAnimate', 'opacity0');
        htmlEl.style.opacity = '1';
        htmlEl.style.transform = 'translateY(0)';
      });
    }, animationConfig.fallbackDelay || 2000);

    return () => clearTimeout(fallbackTimer);
  }, [animationConfig.enableFallback, animationConfig.fallbackDelay]);

  return (
    <div
      ref={hotProductAnimationRef as React.RefObject<HTMLDivElement>}
      className={`blue-light-hot-list ${className}`}
    >
      <div className="blue-light-swiper-box">
        <HotProductSwiper
          title={hotProduct.title}
          products={hotProducts}
          deviceType={deviceType}
          theme={theme}
          onProductClick={handleProductClick}
          onProductExp={handleProductExp}
          ts={Date.now()}
        />
      </div>
    </div>
  );
}
